/**
 * Hallmark cloud-couch configurator.
 *
 * Ported from the Claude Design prototype's reactive model to plain DOM.
 * All catalog data is rendered by the section into a JSON script tag, so the
 * merchant edits pieces, fabrics, colours and rows in the theme editor.
 */
(() => {
  // Every configuration ships exactly one ottoman; the rest are seats. Confirmed
  // against the product photography and the 4-piece / XL copy on the store
  // ("Storage ottoman...", "5PC SECTIONAL + OTTOMAN SET").
  const LAYOUTS = {
    // 3 seats in a row, ottoman off the right end - an L.
    '4': { cols: 3, rows: 2, mods: [
      { t: 'seat', x: 0, y: 0, back: 'top' }, { t: 'seat', x: 1, y: 0, back: 'top' }, { t: 'seat', x: 2, y: 0, back: 'top' },
      { t: 'ott', x: 2, y: 1 }] },
    // 4 seats wrapping the right corner, ottoman in front.
    '5': { cols: 3, rows: 2, mods: [
      { t: 'seat', x: 0, y: 0, back: 'top' }, { t: 'seat', x: 1, y: 0, back: 'top' }, { t: 'seat', x: 2, y: 0, back: 'top' },
      { t: 'seat', x: 2, y: 1, back: 'right' }, { t: 'ott', x: 1, y: 1 }] },
    // 5 seats in a U, ottoman in the middle.
    '6': { cols: 3, rows: 2, mods: [
      { t: 'seat', x: 0, y: 0, back: 'top' }, { t: 'seat', x: 1, y: 0, back: 'top' }, { t: 'seat', x: 2, y: 0, back: 'top' },
      { t: 'seat', x: 0, y: 1, back: 'left' }, { t: 'seat', x: 2, y: 1, back: 'right' }, { t: 'ott', x: 1, y: 1 }] },
  };
  LAYOUTS.xl = LAYOUTS['6'];

  const isLight = (hex) => {
    const n = parseInt(String(hex).slice(1), 16);
    if (Number.isNaN(n)) return true;
    const r = n >> 16, g = (n >> 8) & 255, b = n & 255;
    return (r * 299 + g * 587 + b * 114) / 1000 > 150;
  };

  const textureFor = (fabricId, hex) => {
    const light = isLight(hex);
    const ink = light ? 'rgba(0,0,0,.09)' : 'rgba(255,255,255,.10)';
    const hi = light ? 'rgba(255,255,255,.55)' : 'rgba(255,255,255,.14)';
    if (fabricId === 'velvet') return `radial-gradient(120% 90% at 30% 20%, ${hi} 0%, transparent 55%), linear-gradient(160deg, transparent 40%, ${ink} 100%), ${hex}`;
    if (fabricId === 'linen') return `repeating-linear-gradient(0deg, ${ink} 0 1px, transparent 1px 4px), repeating-linear-gradient(90deg, ${ink} 0 1px, transparent 1px 4px), ${hex}`;
    return `linear-gradient(135deg, ${hi} 0%, transparent 45%), ${hex}`;
  };

  class Configurator {
    constructor(root) {
      this.root = root;
      const raw = root.querySelector('[data-hm-catalog]');
      if (!raw) return;
      try { this.data = JSON.parse(raw.textContent); } catch { return; }
      if (!this.data.rows?.length || !this.data.fabrics?.length) return;

      this.money = (n) => {
        const f = this.data.moneyFormat || '${{amount}}';
        const amount = Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        return f.replace(/\{\{\s*amount[^}]*\}\}/, amount);
      };

      const first = this.data.rows[0];
      this.state = this.resolve({
        pieces: this.data.defaultPieces || first.pieces,
        fabric: this.data.defaultFabric || first.fabric,
        color: this.data.defaultColor || first.color,
        addons: 0,
      });

      this.el = {
        plan: root.querySelector('[data-hm-plan]'),
        planWidth: root.querySelector('[data-hm-plan-width]'),
        planSummary: root.querySelector('[data-hm-plan-summary]'),
        swatchDot: root.querySelector('[data-hm-swatch-dot]'),
        swatchName: root.querySelector('[data-hm-swatch-name]'),
        pieces: root.querySelector('[data-hm-pieces]'),
        fabrics: root.querySelector('[data-hm-fabrics]'),
        fabricNote: root.querySelector('[data-hm-fabric-note]'),
        colors: root.querySelector('[data-hm-colors]'),
        colorName: root.querySelector('[data-hm-color-name]'),
        addonCount: root.querySelector('[data-hm-addon-count]'),
        addonStep: root.querySelector('[data-hm-addon-step]'),
        total: root.querySelector('[data-hm-total]'),
        base: root.querySelector('[data-hm-base]'),
        line: root.querySelector('[data-hm-line]'),
        stock: root.querySelector('[data-hm-stock]'),
        cta: root.querySelector('[data-hm-cta]'),
        library: root.querySelector('[data-hm-library]'),
      };

      root.querySelector('[data-hm-addon-minus]')?.addEventListener('click', () =>
        this.set({ addons: Math.max(0, this.state.addons - 1) }));
      root.querySelector('[data-hm-addon-plus]')?.addEventListener('click', () =>
        this.set({ addons: Math.min(this.data.addonMax || 8, this.state.addons + 1) }));

      this.renderStatic();
      this.render();
    }

    find(pieces, fabric, color) {
      return this.data.rows.find((r) => r.pieces === pieces && r.fabric === fabric && r.color === color);
    }

    /** Snap a requested combination onto a real product. Keep the colour, then the size. */
    resolve(s) {
      if (this.find(s.pieces, s.fabric, s.color)) return s;
      const sameColor = this.data.rows.filter((r) => r.fabric === s.fabric && r.color === s.color);
      if (sameColor.length) return { ...s, pieces: (sameColor.find((r) => r.avail) || sameColor[0]).pieces };
      const samePieces = this.data.rows.filter((r) => r.pieces === s.pieces && r.fabric === s.fabric);
      if (samePieces.length) return { ...s, color: (samePieces.find((r) => r.avail) || samePieces[0]).color };
      const anyFabric = this.data.rows.filter((r) => r.fabric === s.fabric);
      const pick = anyFabric.find((r) => r.avail) || anyFabric[0];
      return pick ? { ...s, pieces: pick.pieces, color: pick.color } : s;
    }

    set(patch) {
      let next = { ...this.state, ...patch };
      if (patch.fabric && !patch.color) {
        const f = this.data.fabrics.find((x) => x.id === patch.fabric);
        if (f && !f.colors.some((c) => c.name === next.color)) next.color = f.colors[0]?.name;
      }
      this.state = this.resolve(next);
      this.render();
    }

    get current() {
      const piece = this.data.pieces.find((p) => p.id === this.state.pieces) || this.data.pieces[0];
      const fabric = this.data.fabrics.find((f) => f.id === this.state.fabric) || this.data.fabrics[0];
      const color = fabric.colors.find((c) => c.name === this.state.color) || fabric.colors[0];
      const row = this.find(piece.id, fabric.id, color.name);
      return { piece, fabric, color, row };
    }

    renderStatic() {
      // Fabric pills
      if (this.el.fabrics) {
        this.el.fabrics.innerHTML = '';
        this.data.fabrics.forEach((f) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'hm-cfg__pill';
          b.textContent = f.name;
          b.dataset.fabric = f.id;
          b.addEventListener('click', () => this.set({ fabric: f.id }));
          this.el.fabrics.appendChild(b);
        });
      }

      // Fabric library cards
      if (this.el.library) {
        this.el.library.innerHTML = '';
        this.data.fabrics.forEach((f) => {
          const card = document.createElement('div');
          card.className = 'hm-fabrics__card';
          const head = document.createElement('div');
          head.className = 'hm-fabrics__card-head';
          head.innerHTML = `<span class="hm-fabrics__name">${f.name}</span><span class="hm-fabrics__count">${f.colors.length} colors</span>`;
          const note = document.createElement('div');
          note.className = 'hm-fabrics__note';
          note.textContent = f.note || '';
          const tiles = document.createElement('div');
          tiles.className = 'hm-fabrics__tiles';
          f.colors.forEach((c) => {
            const t = document.createElement('button');
            t.type = 'button';
            t.className = 'hm-fabrics__tile';
            t.title = `${c.name} ${f.name}`;
            t.setAttribute('aria-label', `${c.name} ${f.name}`);
            t.style.background = textureFor(f.id, c.hex);
            t.dataset.fabric = f.id;
            t.dataset.color = c.name;
            t.innerHTML = `<span class="hm-fabrics__tile-name">${c.name}</span>`;
            t.addEventListener('click', () => {
              this.set({ fabric: f.id, color: c.name });
              this.root.querySelector('[data-hm-plan]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
            tiles.appendChild(t);
          });
          card.append(head, note, tiles);
          this.el.library.appendChild(card);
        });
      }
    }

    render() {
      const { piece, fabric, color, row } = this.current;
      const fill = textureFor(fabric.id, color.hex);
      const available = !!(row && row.avail);
      const base = row ? row.price : 0;

      // Which colourways take extra armless seats changes with what the
      // manufacturer is shipping, so it is read from one setting rather than
      // flagged per row. Anywhere else, hide step 4 and drop any count already
      // picked, so the total never quotes a module we cannot get.
      const ways = String(this.data.addonColorways || '')
        .split(',').map((w) => w.trim()).filter(Boolean);
      const canAddon = ways.includes(`${color.name} ${fabric.name}`.toLowerCase());
      if (this.el.addonStep) this.el.addonStep.hidden = !canAddon;
      if (!canAddon && this.state.addons) this.state.addons = 0;

      const total = base + this.state.addons * (this.data.addonPrice || 0);

      // Floor plan
      if (this.el.plan) {
        const lay = LAYOUTS[piece.id] || LAYOUTS['4'];
        const scale = piece.id === 'xl' ? 1 : 0.86;
        const cellW = 100 / lay.cols, cellH = 100 / lay.rows;
        const seam = isLight(color.hex) ? 'rgba(0,0,0,.18)' : 'rgba(255,255,255,.22)';
        this.el.plan.style.aspectRatio = `${lay.cols} / ${lay.rows}`;
        this.el.plan.innerHTML = '';
        lay.mods.forEach((m, i) => {
          const pad = (1 - scale) / 2;
          const d = document.createElement('div');
          d.className = 'hm-cfg__mod';
          Object.assign(d.style, {
            left: `${(m.x + pad) * cellW}%`, top: `${(m.y + pad) * cellH}%`,
            width: `${cellW * scale}%`, height: `${cellH * scale}%`,
            background: fill, borderRadius: m.t === 'ott' ? '14%' : '10%',
            boxShadow: `inset 0 0 0 1px ${seam}, 0 10px 24px -12px rgba(0,0,0,.45)`,
            transitionDelay: `${i * 40}ms`,
          });
          if (m.back) {
            const b = document.createElement('div');
            b.className = 'hm-cfg__back';
            const pos = m.back === 'top'
              ? { left: '6%', right: '6%', top: '6%', height: '22%' }
              : m.back === 'left'
                ? { top: '6%', bottom: '6%', left: '6%', width: '22%' }
                : { top: '6%', bottom: '6%', right: '6%', width: '22%' };
            Object.assign(b.style, pos, {
              background: fill, borderRadius: '30%',
              boxShadow: `inset 0 0 0 1px ${seam}, 0 4px 10px -4px rgba(0,0,0,.35)`,
            });
            d.appendChild(b);
          }
          this.el.plan.appendChild(d);
        });
      }

      if (this.el.planWidth) this.el.planWidth.textContent = piece.width || '';
      if (this.el.planSummary) this.el.planSummary.textContent = `${piece.label} · ${color.name} ${fabric.name}`;
      if (this.el.swatchDot) this.el.swatchDot.style.background = fill;
      if (this.el.swatchName) this.el.swatchName.textContent = `${color.name} ${fabric.name}`;
      if (this.el.colorName) this.el.colorName.textContent = color.name;
      if (this.el.fabricNote) this.el.fabricNote.textContent = fabric.note || '';
      if (this.el.addonCount) this.el.addonCount.textContent = String(this.state.addons);

      // Piece cards — availability depends on the current fabric + colour
      if (this.el.pieces) {
        this.el.pieces.innerHTML = '';
        this.data.pieces.forEach((p) => {
          const r = this.find(p.id, fabric.id, color.name);
          const offered = !!r;
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'hm-cfg__card' + (p.id === piece.id ? ' is-active' : '') + (offered ? '' : ' is-off');
          b.disabled = !offered;
          b.innerHTML =
            `<span class="hm-cfg__card-label">${p.label}</span>` +
            `<span class="hm-cfg__card-sub">${p.sub || ''}</span>` +
            `<span class="hm-cfg__card-foot">` +
              `<span class="hm-cfg__card-money">${offered ? this.money(r.price) : 'Not offered'}</span>` +
              `<span class="hm-cfg__card-note">${offered ? (r.avail ? 'In stock' : 'Sold out') : `in ${color.name} ${fabric.name}`}</span>` +
            `</span>`;
          if (offered) b.addEventListener('click', () => this.set({ pieces: p.id }));
          this.el.pieces.appendChild(b);
        });
      }

      // Fabric pill active state
      this.el.fabrics?.querySelectorAll('[data-fabric]').forEach((b) => {
        b.classList.toggle('is-active', b.dataset.fabric === fabric.id);
      });

      // Colour swatches — dimmed when not made in the current size
      if (this.el.colors) {
        this.el.colors.innerHTML = '';
        fabric.colors.forEach((c) => {
          const r = this.find(piece.id, fabric.id, c.name);
          const inSize = !!r;
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'hm-cfg__swatch' + (c.name === color.name ? ' is-active' : '') + (inSize ? '' : ' is-other');
          const label = inSize ? (r.avail ? c.name : `${c.name} · sold out`) : `${c.name} · other sizes`;
          b.title = label;
          b.setAttribute('aria-label', label);
          b.style.background = textureFor(fabric.id, c.hex);
          b.addEventListener('click', () => this.set({ color: c.name }));
          this.el.colors.appendChild(b);
        });
      }

      // Library tile active state
      this.el.library?.querySelectorAll('[data-color]').forEach((t) => {
        t.classList.toggle('is-active', t.dataset.fabric === fabric.id && t.dataset.color === color.name);
      });

      // Totals + call to action
      if (this.el.total) this.el.total.textContent = this.money(total);
      if (this.el.base) {
        const addonLabel = this.state.addons
          ? `+${this.state.addons} armless seat${this.state.addons > 1 ? 's' : ''} (${this.money(this.state.addons * this.data.addonPrice)})`
          : 'No add-ons';
        this.el.base.textContent = `${this.money(base)} base · ${addonLabel}`;
      }
      if (this.el.line) this.el.line.textContent = row?.line ? `Your build · ${row.line}` : 'Your build';
      if (this.el.stock) {
        const msg = row
          ? (row.avail ? this.data.inStockText : this.data.soldOutText)
          : 'Not offered in this size';
        this.el.stock.textContent = msg;
        this.el.stock.classList.toggle('is-out', !available);
      }
      if (this.el.cta) {
        this.el.cta.href = row?.url || this.data.fallbackUrl || '#';
        this.el.cta.classList.toggle('is-ghost', !available);
        const label = this.el.cta.querySelector('[data-hm-cta-label]');
        if (label) label.textContent = available ? this.data.ctaInStock : this.data.ctaSoldOut;
      }
    }
  }

  // A WeakSet rather than a data- attribute: an attribute survives innerHTML
  // round-trips, so a re-rendered section carrying it would never re-bind its
  // listeners. Identity is the only thing that reliably tracks a live element.
  const bound = new WeakSet();
  const init = (el) => { if (!bound.has(el)) { bound.add(el); new Configurator(el); } };
  const boot = (scope) => {
    const root = scope && scope.querySelectorAll ? scope : document;
    if (root.matches && root.matches('[data-hm-configurator]')) init(root);
    root.querySelectorAll('[data-hm-configurator]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  // The theme editor re-renders a section in place, then fires this on it.
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
